import { useState, KeyboardEvent, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import {
  Flex,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Box,
  Button,
  Spinner,
  InputGroup,
  Input,
  Stack,
  Collapse,
  IconButton,
  Tooltip,
  Select,
} from '@chakra-ui/react';

import {
  AddIcon,
  SearchIcon,
  SmallCloseIcon,
  RepeatIcon,
} from '@chakra-ui/icons';

import Paginate from 'components/paginate/Paginate';
import Card from 'components/card/Card';
import SelectSearch from 'components/fields/SelectSearch';

import { apiGetWorkflows, apiGetWorkflowLeadSources } from 'api';

import { useUrlFilters } from 'hooks/useUrlFilters';

import type {
  APIPaginatedResponse,
  APIWorkflowListResultData,
  APIWorkflowLeadSourceData,
} from 'types/api.types';

import { WORKFLOW_TYPE_OPTIONS } from 'variables/options';

const TABLE_HEADERS = [
  'Name',
  'Type',
  'Lead Source',
  'Active',
  'Created',
  'Updated',
];

const WORKFLOW_FILTER_KEYS = ['name', 'type', 'is_active', 'lead_source'] as const;

const WorkflowListView = () => {
  const navigate = useNavigate();
  const {
    setFilters,
    page,
    setPage,
    resetAll,
    queryString,
    searchParams,
  } = useUrlFilters(WORKFLOW_FILTER_KEYS);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');

  const [showSearchBar, setShowSearchBar] = useState(false);
  const [nameSearch, setNameSearch] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedActive, setSelectedActive] = useState('');
  const [selectedLeadSource, setSelectedLeadSource] = useState('');

  useEffect(() => {
    setNameSearch(searchParams.get('name') ?? '');
    setSelectedType(searchParams.get('type') ?? '');
    setSelectedActive(searchParams.get('is_active') ?? '');
    setSelectedLeadSource(searchParams.get('lead_source') ?? '');
  }, [searchParams]);

  const { error, data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['workflow-list', page, queryString],
    queryFn: () => apiGetWorkflows(page + 1, queryString),
  }) as {
    data: APIPaginatedResponse<APIWorkflowListResultData>;
    isLoading: boolean;
    error: any;
    isFetching: boolean;
    refetch: () => void;
  };

  const { data: leadSourcesData = [], isLoading: isLoadingLeadSources } =
    useQuery({
      queryKey: ['workflow-lead-sources'],
      queryFn: () => apiGetWorkflowLeadSources(),
      staleTime: 5 * 60 * 1000,
    }) as {
      data: APIWorkflowLeadSourceData[];
      isLoading: boolean;
      error: any;
    };

  const handleCreateWorkflow = () => {
    navigate('/admin/settings/workflows/new');
  };

  const handleSearch = () => {
    setFilters({
      name: nameSearch.trim(),
      type: selectedType.trim(),
      is_active: selectedActive.trim(),
      lead_source: selectedLeadSource.trim(),
    });
  };

  const resetSearch = () => {
    setNameSearch('');
    setSelectedType('');
    setSelectedActive('');
    setSelectedLeadSource('');
    resetAll();
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (error) {
    return (
      <Card>
        <Box p="20px" pb="0">
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{JSON.stringify(error)}</AlertDescription>
          </Alert>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Workflows
        </Text>
        <Flex gap={2}>
          <Tooltip
            label={showSearchBar ? 'Hide search' : 'Show search'}
            hasArrow
          >
            <IconButton
              aria-label={showSearchBar ? 'Hide search' : 'Show search'}
              icon={<SearchIcon />}
              variant={showSearchBar ? 'solid' : 'ghost'}
              colorScheme={showSearchBar ? 'yellow' : 'gray'}
              onClick={() => setShowSearchBar(!showSearchBar)}
              size="md"
            />
          </Tooltip>
          <Tooltip label="Refresh workflows" hasArrow>
            <IconButton
              aria-label="Refresh workflows"
              icon={<RepeatIcon />}
              onClick={refetch}
              variant="ghost"
              isLoading={isLoading || isFetching}
            />
          </Tooltip>
          <Button
            leftIcon={<AddIcon />}
            variant="brand"
            size="md"
            onClick={handleCreateWorkflow}
          >
            New Workflow
          </Button>
        </Flex>
      </Flex>

      <Collapse in={showSearchBar} animateOpacity>
        <Box
          px="20px"
          pb="20px"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <Stack
            spacing={4}
            direction={{ base: 'column', lg: 'row' }}
            align="flex-end"
          >
            <Box flex="1">
              <InputGroup>
                <Input
                  variant="main"
                  placeholder="Search by name"
                  value={nameSearch}
                  onChange={(e) => setNameSearch(e.target.value)}
                  bg={inputBg}
                  size="md"
                  onKeyDown={handleKeyDown}
                />
              </InputGroup>
            </Box>

            <Box flex="1">
              <Select
                variant="main"
                placeholder="All types"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                bg={inputBg}
                size="md"
                onKeyDown={handleKeyDown}
              >
                {WORKFLOW_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Box>

            <Box flex="1">
              <Select
                variant="main"
                placeholder="Active status"
                value={selectedActive}
                onChange={(e) => setSelectedActive(e.target.value)}
                bg={inputBg}
                size="md"
                onKeyDown={handleKeyDown}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Select>
            </Box>

            <Box flex="1">
              <SelectSearch
                options={leadSourcesData.map((source) => ({
                  value: source.id.toString(),
                  label: source.name,
                }))}
                value={selectedLeadSource}
                onChange={(value) =>
                  setSelectedLeadSource(value?.toString() || '')
                }
                placeholder="All sources"
                isDisabled={isLoadingLeadSources}
                allowClear
              />
            </Box>

            <Flex gap={2}>
              <Button
                onClick={handleSearch}
                leftIcon={<SearchIcon />}
                size="md"
                variant="brand"
              >
                Search
              </Button>
              <Button
                onClick={resetSearch}
                variant="outline"
                leftIcon={<SmallCloseIcon />}
                size="md"
              >
                Reset
              </Button>
            </Flex>
          </Stack>
        </Box>
      </Collapse>

      <Table variant="simple" color="gray.500">
        <Thead>
          <Tr>
            {TABLE_HEADERS.map((header) => (
              <Th
                key={header}
                pe="10px"
                borderColor={borderColor}
                color="gray.400"
              >
                {header}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading && (
            <Tr>
              <Td colSpan={6} textAlign="center">
                <Flex align="center" justify="center" mt="20px">
                  <Spinner size="sm" mr="10px" /> Loading workflows...
                </Flex>
              </Td>
            </Tr>
          )}

          {data?.results.map((workflow) => (
            <Tr
              key={workflow.id}
              cursor="pointer"
              _hover={{
                bg: 'gray.200',
              }}
              onClick={() => {
                navigate(`/admin/settings/workflows/edit/${workflow.id}`);
              }}
            >
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {workflow.name}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {workflow.type_display}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {workflow.lead_source?.name || 'N/A'}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {workflow.is_active ? 'Yes' : 'No'}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm">
                    {new Date(workflow.created_at).toLocaleDateString()}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm">
                    {new Date(workflow.updated_at).toLocaleDateString()}
                  </Text>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {data && data.results.length === 0 && (
        <Box textAlign="center" w="100%" mt="20px">
          No workflows found
        </Box>
      )}

      <Paginate page={page} count={data?.count || 0} onSetPage={setPage} />
    </Card>
  );
};

export default WorkflowListView;
